import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider
} from '@mui/material';
import { ArrowForward, Refresh } from '@mui/icons-material';
import { useRecentActivities } from '../../hooks/useActivityLogs';
import ActivityItem from './ActivityItem';

/**
 * RecentActivityWidget - Compact widget for dashboard showing recent activities
 */
const RecentActivityWidget = ({
  orgId,
  limit = 10,
  onViewAll,
  title = 'Recent Activity',
  height = '400px'
}) => {
  const { activities, loading, error, refetch } = useRecentActivities(orgId, limit, true);

  return (
    <Paper
      elevation={2}
      sx={{
        height: height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        <Button
          variant="text"
          size="small"
          startIcon={<Refresh />}
          onClick={refetch}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Activity List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#fafafa'
        }}
      >
        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Box sx={{ padding: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && activities.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography variant="body1" color="textSecondary">
              No recent activities
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Activities will appear here once actions are performed
            </Typography>
          </Box>
        )}

        {/* Activities List */}
        {!loading && !error && activities.length > 0 && (
          <Box>
            {activities.map((activity, index) => (
              <ActivityItem
                key={activity.id || index}
                activity={activity}
                showCategory={true}
                compact={true}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer - View All Button */}
      {onViewAll && !loading && activities.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              padding: 1.5,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              variant="text"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              size="small"
            >
              View All Activities
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default RecentActivityWidget;
